/**
 * ThemeToggle — 관리자 테마를 Light/Dark 두 값으로만 관리한다.
 *
 * 저장값:
 *   - admin-theme-mode: 'light' | 'dark'
 *
 * 레거시 값 정규화:
 *   - light, light-*, *-light -> light
 *   - dark, dark-*, *-dark -> dark
 *   - system, default, unknown, empty -> dark
 */

type ThemeMode = "light" | "dark";

const LS_MODE = "admin-theme-mode";
const LS_LEGACY = "admin-theme";

function legacyVariantKey(suffix: "light" | "dark"): string {
  return ["admin", "theme", suffix].join("-");
}

function normalizeTheme(value: string | null): ThemeMode | null {
  const v = (value ?? "").trim().toLowerCase();
  if (!v || v === "system" || v === "default") return null;
  if (v === "light" || v.startsWith("light-") || v.endsWith("-light")) return "light";
  if (v === "dark" || v.startsWith("dark-") || v.endsWith("-dark")) return "dark";
  return null;
}

function getStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function persistTheme(theme: ThemeMode): void {
  try {
    localStorage.setItem(LS_MODE, theme);
    localStorage.removeItem(LS_LEGACY);
    localStorage.removeItem(legacyVariantKey("light"));
    localStorage.removeItem(legacyVariantKey("dark"));
  } catch {
    // localStorage can be unavailable in private or restricted contexts.
  }
}

function readTheme(): ThemeMode {
  return (
    normalizeTheme(getStored(LS_MODE)) ??
    normalizeTheme(getStored(LS_LEGACY)) ??
    "dark"
  );
}

function applyTheme(theme: ThemeMode): void {
  const root = document.documentElement;
  root.setAttribute("data-admin-theme", theme);
  root.setAttribute("data-theme", theme);
  root.style.colorScheme = theme;

  if (document.body) {
    document.body.setAttribute("data-admin-theme", theme);
    document.body.setAttribute("data-theme", theme);
  }

  syncAriaState(theme);
}

function syncAriaState(theme: ThemeMode): void {
  document.querySelectorAll<HTMLElement>("[data-theme-mode]").forEach(el => {
    el.setAttribute("aria-checked", String(el.dataset.themeMode === theme));
  });
}

function setTheme(theme: ThemeMode): void {
  persistTheme(theme);
  applyTheme(theme);
}

export function initAdminThemeToggle(): void {
  const initial = readTheme();
  persistTheme(initial);
  applyTheme(initial);

  document.querySelectorAll<HTMLElement>("[data-theme-mode]").forEach(el => {
    el.addEventListener("click", () => {
      const next = normalizeTheme(el.dataset.themeMode ?? null);
      if (!next) return;
      setTheme(next);

      const menu = el.closest("details");
      if (menu) menu.removeAttribute("open");
    });
  });
}
